import SubscriptionDropinContainer from '@components/Dropin/SubscriptionDropinContainer'
import useLanguage from '@hooks/useLanguage.hook'
import { lotRoute } from '@views/Lot/route'
import { DateTime } from 'luxon'
import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_SIZE } from 'parkdepot-shared/components/Buttons/types'
import Spinner from 'parkdepot-shared/components/Spinner'
import { useStyletron } from 'parkdepot-shared/theme'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import DropinContainer from '../../../../components/Dropin/DropinContainer'
import PaymentError from '../../../../components/PaymentError/PaymentError.component'
import {
    ButtonContainer,
    ContentContainer,
    ErrorIcon,
    FullyBookedContainer
} from '../../../../components/PaymentError/PaymentError.style'
import Text from '../../../../components/style/Text.style'
import { CheckoutContext } from '../../../../context/CheckoutContext/checkout.context'
import { useGetUser } from '../../../../hooks/useAuth'
import {
    SESSION_STORAGE_setPaymentMethod,
    SESSION_STORAGE_setReferenceNumber
} from '../../../../sessionStorage/checkoutData.sessionStorage'
import { AdyenResultStatus } from '../../../../types/adyenResultStatus.enum'
import { Checkout, PaymentMethod } from '../../../../types/checkout.interface'
import { ActiveFeature, LotViewName } from '../../../../types/lot.interface'
import { LotPricing } from '../../../../types/userData.interface'
import { checkForDaylightSavingsTime } from '../../../../utils/checkForDaylightSavingsTime.utils'
import { getActiveFeatureConfig } from '../../../../utils/getActiveFeature.utils'
import { getLocaleForAdyen } from '../../../../utils/getLocaleForAdyen.utils'
import { getLotPricingMemoized } from '../../../../utils/getLotPricing.utils'
import { paymentSuccessRoute } from '../../../PaymentSuccess/route'
import { ErrorContainer } from '../../Checkout.style'
import { successfulPaymentStatuses } from '../../constants/paymentStatuses.const'
import { useBookingManagement } from '../../hooks/useBookingManagement.hook'
import { LoadingContainer } from '../../style/LoadingContainer.style'

const getNightParkingConfig = (feature: ActiveFeature | null) => {
    if (!feature) {
        return { rrule: '', duration: '' }
    }

    if (feature.config.night_parking_times) {
        return feature.config.night_parking_times.map((nightParkingTime) => ({
            rrule: nightParkingTime.rrule,
            iso_duration: nightParkingTime.iso_duration
        }))
    }

    return [
        {
            rrule: feature.config.rrule || '',
            iso_duration: feature.config.iso_duration || ''
        }
    ]
}

const PaymentStep = () => {
    const { t } = useTranslation()
    const [lng] = useLanguage()
    const [, { typography }] = useStyletron()
    const history = useHistory()

    const [loading, setLoading] = useState(false)
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PaymentMethod.CARD)
    const [errorKey, setErrorKey] = useState<string | null>(null)

    const { checkout: checkoutState } = useContext(CheckoutContext)
    const { user } = useGetUser()

    const {
        checkout,
        successfulBooking,
        bookingId,
        referenceNumber,
        unavailableDates,
        createBooking,
        modifyAndSetCheckout
    } = useBookingManagement()

    const onBookingError = (error: any) => {
        // TODO: handle error
    }

    const onSubscriptionError = (error: any) => {
        if (error.message === 'subscription_exists') {
            setErrorKey('errors.subscriptionExists')
        } else if (error.message === 'lot_is_full') {
            setErrorKey('errors.lotIsFull')
        }

        setLoading(false)
    }

    const onPaymentCompleted = (resultcode: AdyenResultStatus) => {
        if (successfulPaymentStatuses.includes(resultcode.toUpperCase() as AdyenResultStatus)) {
            const newCheckout = {
                ...checkout,
                paymentStatus: resultcode,
                selectedVehicles: checkoutState?.selectedVehicles,
                email: checkoutState?.email,
                bookingNo: referenceNumber,
                paymentMethod
            }
            modifyAndSetCheckout(newCheckout as Checkout)
            history.push(`${paymentSuccessRoute()}?booking=${bookingId}`)
            return
        } else {
            setTimeout(() => {
                modifyAndSetCheckout({ ...(checkout as Checkout), paymentStatus: resultcode })
            }, 3000)
        }
    }

    const onPaymentMethodChange = (method: PaymentMethod) => {
        setPaymentMethod(method)
        SESSION_STORAGE_setPaymentMethod(method)
        SESSION_STORAGE_setReferenceNumber(referenceNumber)
    }

    const getStartAndEndDatesObject = () => {
        const { start: start_datetime, end: end_datetime } = checkForDaylightSavingsTime(
            checkout?.start_datetime,
            checkout?.end_datetime
        )

        return { start_datetime, end_datetime }
    }

    // for daylights saving time
    const getNightlyStartTime = (startDatetime: DateTime, endDatetime: DateTime): string => {
        const startDatetimeString =
            endDatetime.offset < startDatetime.offset ? startDatetime.setZone('UTC+1').toISO() : startDatetime.toISO()

        return startDatetimeString as string
    }

    const createBookingApi = useCallback(() => {
        if (!checkout || !checkoutState || !checkoutState.selectedVehicles || !checkoutState.email) return
        if (checkout.lotCheckoutType === LotViewName.SUBSCRIPTION) return
        setLoading(true)

        const lotPricingType: LotPricing = getLotPricingMemoized(user, checkout?.lot?.id)
        const plates = checkoutState.selectedVehicles.map((vehicle) => ({
            plate: vehicle.plate
        }))

        const apiVariables = {
            email: checkoutState.email,
            plates,
            lot_id: checkout?.lot?.id || '',
            total_amount: checkout?.price || 0,
            shopper_locale: getLocaleForAdyen(lng),
            pricing_type: lotPricingType.pricing_type,
            ...(checkout.lotCheckoutType === LotViewName.HOURLY && { duration_hours: checkout?.duration }),
            ...(checkout.lotCheckoutType === LotViewName.DAILY && {
                ...getStartAndEndDatesObject()
            }),
            ...(checkout.lotCheckoutType === LotViewName.NIGHTLY && {
                // start_datetime: (checkout?.start_datetime as DateTime).setZone('UTC+1').toISO(),
                start_datetime: getNightlyStartTime(
                    checkout?.start_datetime as DateTime,
                    checkout?.end_datetime as DateTime
                ),
                end_datetime: (checkout?.end_datetime as DateTime).toISO(),
                night_parking_config: getNightParkingConfig(
                    getActiveFeatureConfig(checkout.lot?.active_features || [], LotViewName.NIGHTLY)
                )
            })
        }

        if (createBooking) {
            // eslint-disable-next-line promise/catch-or-return
            createBooking({
                variables: {
                    ...apiVariables
                }
            })
                .then(() => {})
                .catch(() => {})
                .finally(() => {
                    setLoading(false)
                })
        }
    }, [checkout, checkoutState, lng, user, createBooking])

    const updateDatesAndGoBack = () => {
        history.goBack()
    }

    const goToLotPage = () => {
        const nextRoute = checkoutState?.lot?.id ? lotRoute(checkoutState?.lot?.id) : '/'
        history.push(nextRoute)
    }

    useEffect(() => {
        if (checkout && successfulPaymentStatuses.includes(checkout.paymentStatus?.toUpperCase() as AdyenResultStatus))
            return
        createBookingApi()
    }, [createBookingApi, checkout])

    if (unavailableDates && unavailableDates.length > 0) {
        return (
            <PaymentError
                onBackClick={updateDatesAndGoBack}
                paymentStatus={AdyenResultStatus.ERROR}
                unAvailableDates={unavailableDates}
            />
        )
    }

    if (errorKey && !checkout?.lotCheckoutType) {
        return (
            <div>
                <ContentContainer>
                    <ErrorIcon className="wemolo-warning-circle" />
                    <FullyBookedContainer>
                        <Text typography={typography.ParagraphMedium} fontWeight={400} $align="center">
                            {t(errorKey)}
                        </Text>
                    </FullyBookedContainer>

                    <ButtonContainer>
                        <Button
                            onClick={goToLotPage}
                            $size={BUTTON_SIZE.medium}
                            overrides={{
                                BaseButton: {
                                    style: {
                                        width: '100%'
                                    }
                                }
                            }}
                        >
                            {t('common.back')}
                        </Button>
                    </ButtonContainer>
                </ContentContainer>
            </div>
        )
    }

    if (!checkoutState) return null

    return (
        <div>
            {loading && (
                <LoadingContainer>
                    <Spinner size="lg" theme="primary" />
                </LoadingContainer>
            )}
            {!loading && checkout?.lotCheckoutType !== LotViewName.SUBSCRIPTION && (
                <>
                    {successfulBooking && bookingId ? (
                        <DropinContainer
                            loading={loading}
                            orderId={bookingId}
                            email={checkoutState.email!}
                            onCompleted={(resultCode) => onPaymentCompleted(resultCode as AdyenResultStatus)}
                            onError={(error) => onBookingError(error)}
                            onPaymentMethodChange={onPaymentMethodChange}
                        />
                    ) : (
                        <ErrorContainer>
                            <Text typography={typography.ParagraphMedium}>
                                {t('errors.checkout.dropIn.sessionError')}
                            </Text>
                        </ErrorContainer>
                    )}
                </>
            )}
            {checkout?.lotCheckoutType === LotViewName.SUBSCRIPTION && (
                <SubscriptionDropinContainer
                    onPaymentMethodChange={onPaymentMethodChange}
                    onCompleted={(resultCode) => onPaymentCompleted(resultCode as AdyenResultStatus)}
                    onError={(error) => onSubscriptionError(error)}
                />
            )}
        </div>
    )
}

export default PaymentStep
